<!--
  Número de parcelas de um recebimento novo (chamado 2026-0067).

  Só aparece quando a forma de pagamento escolhida parcela (maxParcelas > 1). Cada opção mostra o
  valor da parcela. Se a forma muda para uma que aceita menos (ou não parcela), o valor volta para
  dentro do limite — nunca fica um "6x" escondido num PIX. O rateio e as datas de verdade são do
  backend (ParcelamentoDeRecebimento.php); aqui é só a escolha.
-->
<template>
  <div v-if="maxParcelas > 1" class="recebimento-parcelas" :data-testid="testid">
    <select-list
      :data-testid="`${testid}-select`"
      :elem-id="elemId"
      :label="t('Parcelas')"
      :placeholder="t('Número de parcelas')"
      :disabled="disabled"
      :auto-select-first="false"
      :itens-select="opcoes"
      :model-value="modelValue"
      @update:model-value="(v: string | number) => emit('update:modelValue', Number(v) || 1)">
    </select-list>
    <small v-if="modelValue > 1" class="field-hint" :data-testid="`${testid}-hint`">
      {{ tp('Serão registrados {n} recebimentos, um por parcela, todos com a data desta venda.', { n: modelValue }) }}
    </small>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { tComParametros } from '@/utils/tComParametros';
import { useI18n } from 'vue-i18n'
import { opcoesDeParcelas } from '@/composables/useParcelamento'

const props = withDefaults(defineProps<{
  modelValue: number
  maxParcelas: number
  /** o valor TOTAL da venda, em centavos */
  valorCentavos: number
  elemId?: string
  testid?: string
  disabled?: boolean
}>(), {
  elemId: 'recebimento_numero_parcelas',
  testid: 'recebimento-parcelas',
  disabled: false,
})

const emit = defineEmits<{ 'update:modelValue': [value: number] }>()

const { t, n } = useI18n()

// Chave nova com parâmetros: nunca mostrar "{n}" cru (utils/tComParametros).
const tp = (chave: string, parametros: Record<string, string | number>): string => tComParametros(t, chave, parametros);

const opcoes = computed(() => opcoesDeParcelas(
  props.valorCentavos,
  props.maxParcelas,
  (valor: number) => n(valor, 'currency'),
  {
    aVista: (valor: string) => tp('À vista ({valor})', { valor }),
    parcelado: (quantas: number, valor: string) => tp('{n}x de {valor}', { n: quantas, valor }),
    ultima: (valor: string) => tp('(última {valor})', { valor }),
  },
))

// A forma mudou e não aceita a escolha: volta para à vista. Nunca vira outro número sozinha —
// trocar 6x por 3x seria uma decisão que a pessoa não tomou.
watch(() => props.maxParcelas, (max) => {
  if (props.modelValue > Math.max(1, max)) emit('update:modelValue', 1)
}, { immediate: true })
</script>

<style scoped>
.recebimento-parcelas {
  margin-top: 12px;
}

.field-hint {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 4px;
}
</style>
